import React, { FC } from 'react';
import { DriftSnackbar, LocalPhoneIcon, MailIcon } from 'drift-ui';
import iconContactMail from '../../assets/iconContactMail.svg';
import { Rep } from '../../../services/repRetrievalService';
import { formatPhoneNumber } from '../../../utils';
import styles from './repStyles.scss';
import Analytics from '@Common/analytics';
import { ContactRepScreen } from '../ContactRepScreen';
import { ModalContent } from '@Common/Modal';
import { FeatureFlags } from '@Common/featuresFlagsTypes';
import { RootState, useAppSelector } from '../../../store';
import { setContactRepSnackbar, setDisplayContactRepScreen } from '../../../store/contactRepForm';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';

interface ServiceRepDetailsProps {
  rep: Rep;
  serviceName: string;
}

export const ServiceRepDetails: FC<ServiceRepDetailsProps> = ({ rep, serviceName }) => {
  const firebaseEvent = (linkType: string) => {
    Analytics.logFirebaseEvent('CONTACT_US', 'ACCOUNT_HELP', 'CLICK', {
      Description: `${serviceName} Rep ${linkType}`,
    });
  };
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const userFeatures = useAppSelector((state) => state.adminShell.userFeatures);
  const isFactoringCard = serviceName === 'Factoring';
  const hasCreateCaseFF = userFeatures.includes(FeatureFlags.CREATE_CASE_FORM);
  const shouldShowCreateCaseForm = isFactoringCard && hasCreateCaseFF;
  const contactRepScreen = useSelector((state: RootState) => state.contactRep.displayContactRepScreen);
  const contactRepSnackbar = useSelector((state: RootState) => state.contactRep.contactRepSnackbar);

  const freightPermissionsEnabled = userFeatures?.includes(FeatureFlags.FREIGHT_PERMISSIONS_WEB);

  const mailTo =
    freightPermissionsEnabled && rep.product === 'Ryan Transportation'
      ? `mailto:${rep.email}?subject=Freight Support`
      : `mailto:${rep.email}`;

  const handleClick = () => {
    dispatch(setDisplayContactRepScreen(true));
  };

  return (
    <div>
      {shouldShowCreateCaseForm ? (
        <div className={styles.repEmailContainer}>
          <MailIcon className={styles.repIcon} />
          <button className={styles.repEmailButton} onClick={handleClick}>
            <div className={styles.repEmail}>{t('message')}</div>
          </button>
        </div>
      ) : (
        <>
          <div className={styles.repName}>Rep: {rep.name}</div>
          {rep?.phone?.length > 0 && (
            <div className={styles.repPhoneContainer}>
              <LocalPhoneIcon className={styles.repIcon} />
              <a
                href={`tel:${formatPhoneNumber(rep.phone)}`}
                onClick={() => firebaseEvent('Phone')}
                className={styles.repPhone}
              >
                {formatPhoneNumber(rep.phone)}
              </a>
            </div>
          )}
          {rep?.email?.length > 0 && (
            <a href={mailTo} className={styles.repEmailContainer} onClick={() => firebaseEvent('Email')}>
              <img alt="email-icon" src={iconContactMail} />
              <div className={styles.repEmail}>{t('message')}</div>
            </a>
          )}
        </>
      )}
      {contactRepScreen && (
        <ModalContent>
          <div className={styles.modal}>
            <ContactRepScreen onClose={() => dispatch(setDisplayContactRepScreen(false))} />
          </div>
        </ModalContent>
      )}
      <div className={styles.snackBar}>
        <DriftSnackbar
          open={contactRepSnackbar === 'success'}
          onClose={() => dispatch(setContactRepSnackbar(undefined))}
          message={t('contact_rep_success')}
          autoHideDuration={5000}
          variant="success"
        />
      </div>
    </div>
  );
};
